import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, Star, Truck } from "lucide-react";
import { HERO_CARDS } from "@/data/products";
import { scrollToId } from "@/lib/scroll";

const VIDEO_SOURCES = [
  { src: "/assets/hero-lip-ritual.webm", type: "video/webm" },
  { src: "/assets/hero-lip-ritual.mp4", type: "video/mp4" },
];

const HEADLINE = ["Eleven Icons.", "Seven Rituals.", "One Sanctuary."];
const SUBTEXT =
  "Skin, hair, body, face, lip, nails & eyes — India's legacy houses and the world's beauty giants on one blushing shelf, from ₹99 rituals to couture indulgence.";

export default function Hero({ data }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const vx = useTransform(sx, (v) => (v - 0.5) * 26);
  const vy = useTransform(sy, (v) => (v - 0.5) * -20);

  const headline = data?.headline_lines?.length ? data.headline_lines : HEADLINE;
  const subtext = data?.subtext || SUBTEXT;
  const videoSources = data?.video_url
    ? [{ src: data.video_url, type: data.video_url.endsWith(".webm") ? "video/webm" : "video/mp4" }]
    : VIDEO_SOURCES;
  const poster = data?.poster_url || HERO_CARDS[3].url;
  const ctaPrimary = data?.cta_primary?.label || "Start Your Ritual";
  const ctaSecondary = data?.cta_secondary?.label || "Explore 7 Rituals";

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)));
    my.set(Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)));
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      ref={ref}
      onMouseMove={onMove}
      className="relative overflow-hidden bg-[radial-gradient(900px_500px_at_85%_0%,rgba(255,45,135,0.10),transparent),radial-gradient(700px_500px_at_0%_100%,rgba(255,111,165,0.12),transparent)]"
    >
      <div className="absolute inset-0 lg:hidden" data-testid="hero-mobile-bg" aria-hidden="true">
        <video key={videoSources[0].src} autoPlay muted loop playsInline poster={poster} className="h-full w-full object-cover">
          {videoSources.map((v) => (
            <source key={v.src} src={v.src} type={v.type} />
          ))}
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-forest/90 via-forest/75 to-forest/95" />
        <div className="absolute inset-0 bg-gold/10" />
      </div>
      <div className="mx-auto grid min-h-screen max-w-7xl items-center gap-10 px-4 pb-16 pt-28 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:pt-24">
        <div className="relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-moss px-4 py-2"
          >
            <Sparkles className="h-3.5 w-3.5 text-gold" />
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold sm:text-[10px]">
              {data?.eyebrow || "Multi-Brand Cosmetic Showroom"}
            </p>
          </motion.div>

          <h1 className="font-display text-5xl font-medium leading-[1.04] tracking-tight text-cream sm:text-6xl lg:text-[4.6rem]">
            {headline.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === headline.length - 1 ? "italic text-gold" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-6 max-w-md text-sm leading-relaxed text-sage sm:text-base"
          >
            {subtext}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-shop"
              onClick={() => scrollToId("edits")}
              className="group flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest shadow-lg shadow-gold/30 transition-transform duration-300 hover:scale-105"
            >
              {ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-rituals"
              onClick={() => scrollToId("categories")}
              className="rounded-full border-2 border-cream/15 px-8 py-[14px] text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              {ctaSecondary}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-5 border-t border-champagne/40 pt-6"
          >
            <div data-testid="hero-trust-rating" className="flex items-center gap-2.5">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-gold text-gold sm:h-5 sm:w-5" />
                ))}
              </div>
              <p className="text-sm text-sage sm:text-base"><span className="font-display text-xl font-bold text-cream sm:text-2xl">4.8</span> · 2L+ happy shoppers</p>
            </div>
            <div data-testid="hero-trust-authentic" className="flex items-center gap-2.5 text-sm text-sage sm:text-base">
              <ShieldCheck className="h-5 w-5 text-gold sm:h-6 sm:w-6" /> 100% Authentic
            </div>
            <div data-testid="hero-trust-shipping" className="flex items-center gap-2.5 text-sm text-sage sm:text-base">
              <Truck className="h-5 w-5 text-gold sm:h-6 sm:w-6" /> Free shipping ₹999+
            </div>
          </motion.div>
        </div>

        <div className="relative z-10 hidden lg:block" data-testid="hero-video-stage">
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-leaf/20 blur-3xl" aria-hidden="true" />
          <motion.div
            initial={{ opacity: 0, y: 70, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-[2.5rem] border border-champagne/60 shadow-2xl shadow-gold/25"
          >
            <motion.div style={{ x: vx, y: vy }} className="h-[380px] sm:h-[480px] lg:h-[600px]">
              <video
                key={videoSources[0].src}
                data-testid="hero-video"
                autoPlay
                muted
                loop
                playsInline
                poster={poster}
                className="h-full w-full scale-110 object-cover"
              >
                {videoSources.map((v) => (
                  <source key={v.src} src={v.src} type={v.type} />
                ))}
              </video>
            </motion.div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-gold/25 via-transparent to-transparent" />
            <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-white/85 px-4 py-2 backdrop-blur-md" data-testid="hero-video-chip">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-cream">{data?.video_chip || "Now Playing — The Lip Ritual"}</span>
            </div>
            <div className="absolute bottom-4 right-4 rounded-full bg-cream/85 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-forest backdrop-blur-md">
              HD 1080
            </div>
          </motion.div>
          <div data-testid="hero-rotating-badge" className="absolute -bottom-6 -left-4 z-20 hidden sm:block" aria-hidden="true">
            <div className="relative flex h-28 w-28 items-center justify-center">
              <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full animate-[spin_18s_linear_infinite]">
                <defs>
                  <path id="badge-circle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                </defs>
                <text className="fill-gold font-mono text-[8px] uppercase" style={{ letterSpacing: "2.6px" }}>
                  <textPath href="#badge-circle">Soonyoung • Botanical Beauty • Est 2026 •</textPath>
                </text>
              </svg>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gold shadow-lg shadow-gold/40">
                <Sparkles className="h-5 w-5 text-forest" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
